// Mirror of backend `models/src/bi/change-set.ts` (the wire types only) and
// of the generated enum `BiChangeSetStatus`.

import { BiDashboardLayout, BiDashboardOp, BiPlacement } from './dashboard';
import { BiVizStyleOption } from './viz';

export enum BiChangeSetStatus {
  Pending = 'pending',
  Applied = 'applied',
  Discarded = 'discarded',
  Stale = 'stale',
}

/** Change-set only: undo the dashboard's current revision. */
export enum BiChangeSetOnlyOpKind {
  Undo = 'undo',
}

export interface BiUndoChangeOp {
  op: BiChangeSetOnlyOpKind.Undo;
  /** The revision undone: the dashboard's revision when proposed. */
  revision: number;
}

/** What a change set holds: dashboard ops, or one undo. */
export type BiChangeSetOp = BiDashboardOp | BiUndoChangeOp;

export enum BiChangeKind {
  CardAdded = 'card_added',
  CardUpdated = 'card_updated',
  CardRemoved = 'card_removed',
  CardMoved = 'card_moved',
  DashboardRenamed = 'dashboard_renamed',
  FiltersChanged = 'filters_changed',
  /** The dashboard's palette: one `Palette` change. */
  PaletteChanged = 'palette_changed',
}

/** What about a card changed; the client words it. */
export enum BiCardChangeField {
  Title = 'title',
  Dataset = 'dataset',
  Measures = 'measures',
  Dimensions = 'dimensions',
  /** A time dimension's grain: `before` / `after` are `BiTimeGrain`s. */
  Grain = 'grain',
  Filters = 'filters',
  /** `before` / `after`: a preset, or `from~to`. */
  TimeRange = 'time_range',
  Sort = 'sort',
  Limit = 'limit',
  /** `before` / `after` are `BiVizType`s. */
  VizType = 'viz_type',
  /** The viz changed in some other way (axes, series, format). */
  Viz = 'viz',
  /**
   * The card became or stopped being a blend, or a blend's merge keys or
   * join changed.
   */
  Blend = 'blend',
  /**
   * One style option (`option`) changed; `before` / `after` say its value
   * in words, null for the theme's (or, for a palette, the dashboard's).
   */
  Style = 'style',
  /** The dashboard's palette: `before` / `after` are `BiPalette`s. */
  Palette = 'palette',
  /**
   * One charted series' type or axis (combo charts): `before` / `after`
   * say it in words, e.g. `amount：柱形，左轴`.
   */
  Series = 'series',
}

export interface BiCardChange {
  field: BiCardChangeField;
  /** With `Style`: the option that changed. */
  option?: BiVizStyleOption;
  before?: string | number | null;
  after?: string | number | null;
}

/** One line of a change set's diff. */
export interface BiChangeEntry {
  kind: BiChangeKind;
  /** The card; for a card being added, absent until it exists. */
  cardId: string | null;
  /** The draft a card is added from. */
  draftId: string | null;
  /** The card's title (as it will read), or the dashboard's name. */
  title: string;
  /** For an update or a rename: what changed. */
  changes: BiCardChange[];
  /** For a move or an add: where it goes. */
  placement?: BiPlacement;
}

export enum BiPreviewCardState {
  Unchanged = 'unchanged',
  Added = 'added',
  Updated = 'updated',
  Moved = 'moved',
  Removed = 'removed',
}

export interface BiPreviewCard {
  /** A new card is placed in the layout under its draft id. */
  id: string;
  draftId: string | null;
  title: string;
  state: BiPreviewCardState;
}

/** The dashboard as it would be once applied, for the ghost preview. */
export interface BiChangeSetPreview {
  layout: BiDashboardLayout;
  /** Every card, removed ones included (they are not in the layout). */
  cards: BiPreviewCard[];
}

export interface BiChangeSetView {
  id: string;
  dashboardId: string;
  baseRevision: number;
  ops: BiChangeSetOp[];
  summary: string;
  /** `stale` also for a pending set past its expiry or whose base moved. */
  status: BiChangeSetStatus;
  appliedRevision: number | null;
  createdBy: number;
  createdAt: string;
  expiresAt: string;
  appliedAt: string | null;
}

/** `GET /api/bi/change-sets/:id`. */
export interface BiChangeSetDetail {
  changeSet: BiChangeSetView;
  dashboard: { id: string; name: string; revision: number };
  changes: BiChangeEntry[];
  /** Only while it can still be applied. */
  preview: BiChangeSetPreview | null;
}
